// Overlays de trial/limite (sub-onda εd + conversão QR) — TV-first (navegável
// por controle, foco visível). Mostrados quando o trial esgota (interrompe o
// vídeo) ou o limite de telas é atingido.
//
// O QR aponta pra /comprar?ctx=<code> (contexto de compra criado no backend) —
// leva o provider/lista que o user usava + identidade (logado vincula direto;
// anônimo cria conta). Fallback gracioso: se a criação do contexto falhar, o QR
// cai pra /planos cru.

import { useTranslation } from '@app-iptv/i18n'
import { pause as pauseSpatial, resume as resumeSpatial } from '@app-iptv/ui'
import { useEffect, useRef, useState } from 'react'

import { api, getSiteUrl } from '../api'
import { useOkKeyOwner } from '../lib/ok-key-owner'
import { useSessionStore } from '../store'

import { QRDisplay } from './QRDisplay'
import styles from './TrialOverlay.module.css'

/** Teclas que NÃO podem vazar pro app de fundo quando o overlay é inescapável:
 *  setas (spatial nav), Enter/OK (ativar), e BACK/ESC (sair). Engolidas em
 *  capture phase — nem o Norigin nav nem o useBackKey das telas de fundo veem. */
function isNavOrActionKey(e: KeyboardEvent): boolean {
  return (
    e.key === 'ArrowUp' ||
    e.key === 'ArrowDown' ||
    e.key === 'ArrowLeft' ||
    e.key === 'ArrowRight' ||
    e.key === 'Enter' ||
    e.key === ' ' ||
    e.key === 'Escape' ||
    e.key === 'Backspace' ||
    e.key === 'GoBack' ||
    e.keyCode === 13 ||
    e.keyCode === 32 ||
    e.keyCode === 10009 || // Tizen BACK
    e.keyCode === 461 // webOS BACK
  )
}

// URL do site vem do runtime-config (env SITE_URL da API) via getSiteUrl(),
// com fallback síncrono (VITE_SITE_URL → literal). NÃO usar const de módulo:
// congelaria o fallback antes do runtime-config carregar no boot. Chamado no
// uso (fallbackUrl roda no catch em runtime; o texto da URL no render do shell).

function fallbackUrl(source: string): string {
  const u = new URL('/planos', getSiteUrl())
  u.searchParams.set('from', source)
  return u.toString()
}

/** Cria o contexto de compra e devolve a URL contextual pro QR.
 *  Fallback pra /planos cru se a chamada falhar (degradação graciosa). */
function usePurchaseQr(source: 'trial' | 'stream_limit'): string | null {
  const [url, setUrl] = useState<string | null>(null)

  useEffect(() => {
    // Cria o contexto UMA vez por montagem do overlay (não a cada mudança de
    // store) — evita acumular contextos órfãos no banco. Lê o store no momento
    // via getState (não como dep reativa).
    let cancelled = false
    const s = useSessionStore.getState()
    void api
      .createPurchaseContext({
        source,
        providerCode: s.providerCode,
        listName: s.direct?.listName ?? null,
        serverUrl: s.direct?.serverUrl ?? null,
        xtreamUsername: s.username,
      })
      .then((r) => {
        if (!cancelled) setUrl(r.url)
      })
      .catch(() => {
        if (!cancelled) setUrl(fallbackUrl(source))
      })
    return () => {
      cancelled = true
    }
  }, [source])

  return url
}

interface OverlayProps {
  onBack: () => void
}

function OverlayShell({
  title,
  text,
  hint,
  source,
  onBack,
}: {
  title: string
  text: string
  /** Linha de destaque (ex: liberação instantânea). Opcional. */
  hint?: string
  source: 'trial' | 'stream_limit'
  /** Quando presente, mostra o botão "Voltar" (overlay fechável). Quando
   *  ausente, o overlay é INESCAPÁVEL (trial esgotado: só comprar ou reiniciar). */
  onBack?: (() => void) | undefined
}): React.JSX.Element {
  const { t } = useTranslation()
  const btnRef = useRef<HTMLButtonElement>(null)
  const cardRef = useRef<HTMLDivElement>(null)
  const qrUrl = usePurchaseQr(source)
  // Host do site (sem protocolo) pro texto exibido — derivado do runtime-config
  // no render (getSiteUrl() nunca undefined).
  const siteHost = getSiteUrl().replace(/^https?:\/\//, '')
  // Inescapável = sem botão Voltar (onBack ausente). Trial esgotado.
  const inescapable = !onBack

  useEffect(() => {
    if (onBack) btnRef.current?.focus()
    else cardRef.current?.focus() // sem botão: foco no card (não fica solto atrás)
  }, [onBack])

  // ESCAPÁVEL (limite de telas): o "Voltar" é um <button> nativo que recebe o
  // foco REAL do DOM acima — mas foco não é ativação. Enquanto o Norigin estiver
  // vivo (e nas telas onde este overlay aparece com botão ele PODE estar: a
  // PlayerScreen não pausa o spatial nav), ele cancela a ação padrão do Enter no
  // window e o click nativo nunca nasce. Pior: com o foco LÓGICO parado num card
  // atrás do overlay, o `onEnterPress` do Norigin ativaria esse card. Mesma
  // classe exata do botão da tela de crash (components/RecoveryBoundary.tsx), e
  // por isso a mesma peça. O ramo inescapável NÃO usa isto: ele já engole todas
  // as teclas em capture logo abaixo.
  useOkKeyOwner(() => onBack?.(), !inescapable)

  // INESCAPÁVEL (trial esgotado): pausa o spatial nav e engole TODAS as teclas de
  // navegação/ação/BACK em capture phase, pra nenhuma vazar pro app de fundo
  // (telas montadas atrás mantêm useBackKey global + Norigin nav ativos). Sem
  // isso, BACK do controle navegaria por trás e setas+Enter ativariam cards de
  // fundo — o bloqueio viraria cosmético. Saída só por QR (celular) ou reiniciar.
  useEffect(() => {
    if (!inescapable) return
    pauseSpatial()
    const onKey = (e: KeyboardEvent): void => {
      if (isNavOrActionKey(e)) {
        e.preventDefault()
        e.stopPropagation()
        // NÃO faz nada: inescapável. Re-foca o card se o foco escapou.
        cardRef.current?.focus()
      }
    }
    window.addEventListener('keydown', onKey, { capture: true })
    document.addEventListener('keydown', onKey, { capture: true })
    return () => {
      window.removeEventListener('keydown', onKey, { capture: true })
      document.removeEventListener('keydown', onKey, { capture: true })
      resumeSpatial()
    }
  }, [inescapable])

  return (
    <div className={styles.overlay} role="dialog" aria-modal="true" aria-label={title}>
      <div className={styles.card} ref={cardRef} tabIndex={-1}>
        <h2 className={styles.title}>{title}</h2>
        <p className={styles.text}>{text}</p>
        {hint ? <p className={styles.hint}>{hint}</p> : null}
        {/* Sem `size`: sai do token --qr-size. Era `size={200}` — quase metade
            do piso de 360 do spec, no QR de COMPRA (a superfície de conversão
            do app), escaneado da mesma distância que o de pareamento. O
            `.card` foi alargado na TV pra o token caber; ver o CSS. */}
        <div className={styles.qr}>{qrUrl ? <QRDisplay value={qrUrl} ariaLabel={t('common.qrPurchaseAria')} /> : null}</div>
        <p className={styles.url}>{siteHost}/comprar</p>
        {onBack ? (
          <button ref={btnRef} className={styles.btn} onClick={onBack}>
            {t('common.back')}
          </button>
        ) : null}
      </div>
    </div>
  )
}

/** Trial esgotado (30min/dia). INESCAPÁVEL — cobre o app inteiro, sem botão de
 *  fechar/voltar. Saída só comprando (QR) ou reiniciando o app (ao reabrir, o
 *  gate re-checa e mostra de novo até comprar ou virar o dia). Decisão Fernando. */
export function TrialExhaustedOverlay(): React.JSX.Element {
  const { t } = useTranslation()
  return (
    <OverlayShell
      title={t('trial.exhaustedTitle')}
      text={t('trial.exhaustedText')}
      hint={t('trial.exhaustedHint')}
      source="trial"
    />
  )
}

/** Limite de telas simultâneas do plano atingido. Fechável (situacional —
 *  basta parar o outro stream). Mantém o "Voltar". */
export function StreamLimitOverlay({ onBack }: OverlayProps): React.JSX.Element {
  const { t } = useTranslation()
  return (
    <OverlayShell
      title={t('trial.streamLimitTitle')}
      text={t('trial.streamLimitText')}
      source="stream_limit"
      onBack={onBack}
    />
  )
}
